import { ChevronDown, Info } from "lucide-react"
import type { ReactNode } from "react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { StatusBadge } from "@/components/status-badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { formatDate, formatNumber } from "@/lib/format"
import { cn } from "@/lib/utils"
import type { Contract } from "@/types/domain"

interface FieldProps {
  label: string
  children: ReactNode
  fullWidth?: boolean
}

function Field({ label, children, fullWidth }: FieldProps) {
  return (
    <div
      className={cn(
        "bg-muted/40 flex flex-col gap-0.5 rounded-md p-3",
        fullWidth ? "sm:col-span-2" : "",
      )}
    >
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="text-sm font-medium">{children || "—"}</span>
    </div>
  )
}

function Section({
  title,
  children,
}: {
  title: string
  children: ReactNode
}) {
  return (
    <div className="space-y-3">
      <h3 className="text-muted-foreground text-sm font-semibold">{title}</h3>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">{children}</div>
    </div>
  )
}

interface ContractDetailsSectionProps {
  contract: Contract
}

export function ContractDetailsSection({ contract }: ContractDetailsSectionProps) {
  const { t, i18n } = useTranslation(["contracts", "common"])
  const [open, setOpen] = useState(true)

  const currency = contract.currency ?? ""

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <Info className="text-muted-foreground size-4" />
          {t("details.title")}
        </CardTitle>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          onClick={() => setOpen((o) => !o)}
          aria-label={t("actions.view", { ns: "common" })}
        >
          <ChevronDown
            className={cn("size-4 transition-transform", open ? "rotate-180" : "")}
          />
        </Button>
      </CardHeader>
      {open ? (
        <CardContent className="grid gap-6 lg:grid-cols-3">
          <Section title={t("details.info")}>
            <Field label={t("details.fields.contractNumber")}>{contract.contractNumber}</Field>
            <Field label={t("details.fields.client")}>{contract.clientName}</Field>
            <Field label={t("details.fields.product")}>
              {contract.productType
                ? t(`options.productTypes.${contract.productType}`, { defaultValue: contract.productType })
                : null}
            </Field>
            <Field label={t("details.fields.quantity")}>
              {formatNumber(contract.quantity ?? 0)} {t("units.mt", { ns: "common" })}
            </Field>
            <Field label={t("details.fields.origin")}>{contract.origin}</Field>
            <Field label={t("details.fields.cocoaGrade")}>
              {contract.cocoaGrade
                ? t(`options.cocoaGrades.${contract.cocoaGrade}`, { defaultValue: contract.cocoaGrade })
                : null}
            </Field>
            <Field label={t("details.fields.signatureDate")}>
              {formatDate(contract.contractDate, i18n.language)}
            </Field>
            <Field label={t("details.fields.createdAt")}>
              {formatDate(contract.created_at, i18n.language)}
            </Field>
          </Section>

          <Section title={t("details.pricesAndPremiums")}>
            {typeof contract.basePrice === "number" ? (
              <Field label={t("details.fields.fixedPrice")}>
                {formatNumber(contract.basePrice, 2)} {currency} {t("units.perKg", { ns: "common" })}
              </Field>
            ) : null}
            {typeof contract.certificationPremiumPerKilo === "number" ? (
              <Field label={t("details.fields.certificationPremium")} fullWidth>
                {formatNumber(contract.certificationPremiumPerKilo, 2)} {currency}{" "}
                {t("units.perKg", { ns: "common" })}
              </Field>
            ) : null}
            {typeof contract.fairtradePremiumPerKilo === "number" ? (
              <Field label={t("details.fields.fairtradePremium")}>
                {formatNumber(contract.fairtradePremiumPerKilo, 2)} {currency}{" "}
                {t("units.perKg", { ns: "common" })}
              </Field>
            ) : null}
            {typeof contract.organicPremiumPerKilo === "number" ? (
              <Field label={t("details.fields.organicPremium")}>
                {formatNumber(contract.organicPremiumPerKilo, 2)} {currency}{" "}
                {t("units.perKg", { ns: "common" })}
              </Field>
            ) : null}
            {typeof contract.rainforestPremiumPerKilo === "number" ? (
              <Field label={t("details.fields.rainforestPremium")}>
                {formatNumber(contract.rainforestPremiumPerKilo, 2)} {currency}{" "}
                {t("units.perKg", { ns: "common" })}
              </Field>
            ) : null}
            {typeof contract.utzPremiumPerKilo === "number" ? (
              <Field label={t("details.fields.utzPremium")}>
                {formatNumber(contract.utzPremiumPerKilo, 2)} {currency}{" "}
                {t("units.perKg", { ns: "common" })}
              </Field>
            ) : null}
            {typeof contract.agroforestryBonusPerKilo === "number" ? (
              <Field label={t("details.fields.otherPremium")}>
                {formatNumber(contract.agroforestryBonusPerKilo, 2)} {currency}{" "}
                {t("units.perKg", { ns: "common" })}
              </Field>
            ) : null}
            {contract.basePrice && contract.quantity ? (
              <Field label={t("details.fields.fixedPriceAmount")} fullWidth>
                {formatNumber(contract.basePrice * contract.quantity * 1000)} {currency}
              </Field>
            ) : null}
          </Section>

          <Section title={t("details.deliveryAndIncoterms")}>
            {contract.currency ? (
              <Field label={t("details.fields.currency")}>{contract.currency}</Field>
            ) : null}
            <Field label={t("details.fields.incoterms")}>
              {contract.incoterms
                ? t(`options.incoterms.${contract.incoterms}`, { defaultValue: contract.incoterms })
                : null}
            </Field>
            <Field label={t("details.fields.portOfLoading")}>{contract.portOfLoading}</Field>
            <Field label={t("details.fields.destinationPort")}>{contract.destinationPort}</Field>
            <Field label={t("details.fields.contractType")}>
              {contract.contractType
                ? t(`options.contractTypes.${contract.contractType}`, { defaultValue: contract.contractType })
                : null}
            </Field>
            {typeof contract.differential === "number" ? (
              <Field label={t("details.fields.differential")}>
                {formatNumber(contract.differential)} {currency} {t("units.perMt", { ns: "common" })}
              </Field>
            ) : null}
            {typeof contract.countryDifferential === "number" ? (
              <Field label={t("details.fields.countryDifferential")}>
                {formatNumber(contract.countryDifferential)} {currency} {t("units.perMt", { ns: "common" })}
              </Field>
            ) : null}
            {typeof contract.floatingDiscount === "number" ? (
              <Field label={t("details.fields.floatingDiscount")}>
                {formatNumber(contract.floatingDiscount)}
              </Field>
            ) : null}
          </Section>

          {contract.pricingMechanism ||
          contract.referenceIndex ||
          contract.referenceMarket ||
          contract.referenceMonth ||
          contract.referenceYear ? (
            <Section title={t("details.marketReferences")}>
              {contract.pricingMechanism ? (
                <Field label={t("details.fields.pricingMechanism")}>
                  {t(`options.pricingMechanisms.${contract.pricingMechanism}`, {
                    defaultValue: contract.pricingMechanism,
                  })}
                </Field>
              ) : null}
              {contract.referenceIndex ? (
                <Field label={t("details.fields.referenceIndex")}>
                  {t(`options.referenceIndexes.${contract.referenceIndex}`, {
                    defaultValue: contract.referenceIndex,
                  })}
                </Field>
              ) : null}
              {contract.referenceMarket ? (
                <Field label={t("details.fields.referenceMarket")}>
                  {t(`options.referenceMarkets.${contract.referenceMarket}`, {
                    defaultValue: contract.referenceMarket,
                  })}
                </Field>
              ) : null}
              {contract.referenceMonth || contract.referenceYear ? (
                <Field label={t("details.fields.referenceMonthYear")} fullWidth>
                  {contract.referenceMonth
                    ? t(`options.months.${contract.referenceMonth}`, {
                        defaultValue: contract.referenceMonth,
                      })
                    : ""}{" "}
                  {contract.referenceYear ?? ""}
                </Field>
              ) : null}
            </Section>
          ) : null}

          {contract.calculationMethod || contract.fixingStartDate || contract.fixingEndDate ? (
            <Section title={t("details.fixingWindow")}>
              {contract.calculationMethod ? (
                <Field label={t("details.fields.calculationMethod")}>
                  {t(`options.calculationMethods.${contract.calculationMethod}`, {
                    defaultValue: contract.calculationMethod,
                  })}
                </Field>
              ) : null}
              {contract.fixingStartDate ? (
                <Field label={t("details.fields.fixingStart")}>
                  {formatDate(contract.fixingStartDate, i18n.language)}
                </Field>
              ) : null}
              {contract.fixingEndDate ? (
                <Field label={t("details.fields.fixingEnd")}>
                  {formatDate(contract.fixingEndDate, i18n.language)}
                </Field>
              ) : null}
            </Section>
          ) : null}

          <Section title={t("details.certifications")}>
            <Field label={t("details.fields.requiredCerts")} fullWidth>
              <div className="flex flex-wrap gap-1.5">
                {(contract.certificates ?? []).length === 0 ? (
                  <span className="text-muted-foreground">—</span>
                ) : (
                  (contract.certificates ?? []).map((cert) => (
                    <StatusBadge key={cert} tone="success">
                      {t(`options.certificates.${cert}`, { defaultValue: cert })}
                    </StatusBadge>
                  ))
                )}
              </div>
            </Field>
          </Section>

          {contract.notes ? (
            <div className="lg:col-span-3">
              <h3 className="text-muted-foreground mb-2 text-sm font-semibold">
                {t("details.notes")}
              </h3>
              <p className="text-sm">{contract.notes}</p>
            </div>
          ) : null}

          {contract.adjustments ? (
            <div className="lg:col-span-3">
              <h3 className="text-muted-foreground mb-2 text-sm font-semibold">
                {t("details.adjustments")}
              </h3>
              <p className="text-sm">
                {typeof contract.adjustments === "string"
                  ? contract.adjustments
                  : JSON.stringify(contract.adjustments)}
              </p>
            </div>
          ) : null}

          <div className="lg:col-span-3">
            <h3 className="text-muted-foreground mb-3 text-sm font-semibold">
              {t("details.supplierCompliance")}
            </h3>
            <div className="grid gap-2 sm:grid-cols-3">
              <Field label={t("details.fields.onlyEUDR")}>
                <StatusBadge tone={contract.onlyAcceptsEUDR ? "success" : "muted"}>
                  {contract.onlyAcceptsEUDR
                    ? t("details.fields.enabled")
                    : t("details.fields.disabled")}
                </StatusBadge>
              </Field>
              <Field label={t("details.fields.onlyCertified")}>
                {contract.onlyAcceptsCertificatesEnabled &&
                contract.onlyAcceptsCertificates &&
                contract.onlyAcceptsCertificates.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {contract.onlyAcceptsCertificates.map((c) => (
                      <StatusBadge key={c} tone="success">
                        {t(`options.certificates.${c}`, { defaultValue: c })}
                      </StatusBadge>
                    ))}
                  </div>
                ) : (
                  <StatusBadge tone="muted">{t("details.fields.soonAvailable")}</StatusBadge>
                )}
              </Field>
              <Field label={t("details.fields.noChildLabor")}>
                <StatusBadge tone="muted">{t("details.fields.soonAvailable")}</StatusBadge>
              </Field>
            </div>
          </div>
        </CardContent>
      ) : null}
    </Card>
  )
}
